<template>
  <div class="relative">
    <img width="634" height="400" :src="img" class="block pointer-events-none" />
    <TransitionGroup name="key-img" appear>
      <img
        v-for="item in keyUrls"
        :key="item"
        width="634"
        height="400"
        :src="item"
        class="absolute inset-0 block pointer-events-none"
      />
    </TransitionGroup>
    <!-- 按键定位 -->
    <template v-if="current">
      <div
        v-for="mouseKey in keyButtons"
        :key="mouseKey.keywords"
        :style="{
          top: mouseKey.y + 'px',
          left: mouseKey.x + 'px',
        }"
        class="absolute inset-0 z-10 w-fit h-fit"
        :class="mouseKey.alignLeft === false ? '-translate-x-full' : ''"
      >
        <Button
          :color="mouseKey.keywords === current.keywords ? 'blue2' : 'glass'"
          size="sm"
          :active="mouseKey.keywords === current.keywords"
          shape="rect"
          class="h-8 py-1.5"
          @click="() => handleClick(mouseKey)"
          @dragover.prevent
          @drop="(event: DragEvent) => handleDrop(event, mouseKey)"
        >
          <slot name="key-label" :mouse-key="mouseKey" :label="getKeyLabel(mouseKey.keywords)">
            {{ getKeyLabel(mouseKey.keywords) }}
          </slot>
        </Button>
      </div>
    </template>
    <template v-else>
      <div
        v-for="mouseKey in keyButtons"
        :key="mouseKey.keywords"
        :style="{
          top: mouseKey.y + 'px',
          left: mouseKey.x + 'px',
        }"
        class="p-2 absolute inset-0 z-10 w-fit h-fit"
        :class="mouseKey.alignLeft === false ? '-translate-x-full' : ''"
      >
        <Button
          color="glass"
          size="sm"
          shape="rect"
          class="h-8 py-1.5"
          @click="() => handleClick(mouseKey)"
          @dragover.prevent
          @drop="(event: DragEvent) => handleDrop(event, mouseKey)"
        >
          <slot name="key-label" :mouse-key="mouseKey" :label="getKeyLabel(mouseKey.keywords)">
            {{ getKeyLabel(mouseKey.keywords) }}
          </slot>
        </Button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
  import type { LayoutKey } from '@rapoo/device-sdk'
  import { Button } from '@rapoo/element'
  import { useLocaleStore } from '@/stores'
  import { getDragData, DragType } from '@/utils/dnd'
  import { MOUSE_KEY_ITEMS_MAP } from '../utils/constants'
  import type { KeyChangePayload } from '../types'

  withDefaults(
    defineProps<{
      // 需要显示的鼠标按键按钮
      keyButtons: LayoutKey[]
      // 需要显示的按键区域图片
      keyUrls: string[]
      // 展示的鼠标大图
      img: string
      // 当前激活的按钮
      current?: LayoutKey
      // 是否可接收拖拽按钮
      draggable?: boolean
    }>(),
    {},
  )

  defineSlots<{
    'key-label'(props: { mouseKey: LayoutKey; label: string }): any
  }>()

  const emits = defineEmits<{
    'key-click': [data: LayoutKey]
    'update-setting': [data: KeyChangePayload, targetKey: LayoutKey]
  }>()

  const { t } = useLocaleStore()

  const handleClick = (data: LayoutKey) => {
    emits('key-click', data)
  }

  const handleDrop = (event: DragEvent, data: LayoutKey) => {
    const dataTransfer = event.dataTransfer
    if (!dataTransfer) return

    event.preventDefault()
    event.stopPropagation()

    const draggedData = getDragData<KeyChangePayload>(dataTransfer, DragType.KEY_MAPPING)
    if (!draggedData) return

    draggedData.keywords = data.keywords
    emits('update-setting', draggedData, data)
  }

  const getKeyLabel = (keywords: string) => {
    const name = MOUSE_KEY_ITEMS_MAP.get(keywords)?.name
    return name ? t(name) : ''
  }
</script>

<style scoped>
  .key-img-enter-active,
  .key-img-leave-active {
    transition: opacity 0.3s ease;
  }

  .key-img-enter-from,
  .key-img-leave-to {
    opacity: 0;
  }
</style>
